/**
 * use-contracts.ts — Hooks cho Platform → Hợp đồng (super-admin) + gọi API công khai của trang ký.
 * Backend: ecdp_backend_source/src/modules/platform/contract/*
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { platformApi } from '@/lib/platform-client'
import { API_BASE } from '@/lib/config'
import type {
  Contract, ContractListItem, PublicContract, TemplateResponse, formToInput,
} from '@/pages/platform/contract/contract-helpers'

const KEY = ['platform', 'contracts'] as const
type ContractInput = ReturnType<typeof formToInput>

export function useContracts(params: { status?: string; q?: string; page?: number }) {
  return useQuery<{ items: ContractListItem[]; total: number; page: number; pageSize: number }>({
    queryKey: [...KEY, 'list', params],
    queryFn: async () => (await platformApi.get('/platform/contracts', { params })).data,
    placeholderData: (prev) => prev,
  })
}

export function useContract(id: string | undefined) {
  return useQuery<Contract>({
    queryKey: [...KEY, 'one', id],
    enabled: !!id,
    queryFn: async () => (await platformApi.get(`/platform/contracts/${id}`)).data,
  })
}

export function useContractTemplate() {
  return useQuery<TemplateResponse>({
    queryKey: [...KEY, 'template'],
    queryFn: async () => (await platformApi.get('/platform/contracts/template')).data,
    staleTime: 60_000,
  })
}

export function useSaveContractTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (template: Record<string, unknown>): Promise<TemplateResponse> =>
      (await platformApi.put('/platform/contracts/template', { template })).data,
    onSuccess: (data) => qc.setQueryData([...KEY, 'template'], data),
  })
}

export function useSaveContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ContractInput }): Promise<Contract> =>
      (id ? await platformApi.put(`/platform/contracts/${id}`, input) : await platformApi.post('/platform/contracts', input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useSendContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, expiresDays }: { id: string; expiresDays?: number }) =>
      (await platformApi.post<{ contract: Contract; signUrl: string; emailSent: boolean }>(`/platform/contracts/${id}/send`, expiresDays ? { expiresDays } : {})).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useCancelContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }): Promise<Contract> =>
      (await platformApi.post(`/platform/contracts/${id}/cancel`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export async function loadQuotePrefill(quoteId: string) {
  return (await platformApi.get<{
    quoteId: string; quoteNumber: string; orgId: string | null
    customer: { fullName: string; idNumber: string; address: string; email: string; phone: string }
    deal: { description: string; amount: number | null }
  }>(`/platform/contracts/from-quote/${quoteId}`)).data
}

/**
 * Tải PDF (cần header Authorization nên đi qua axios → blob). `kind` = pdf (đã ký) | preview-pdf (xem trước).
 * Xem trước: mở tab mới NGAY trong lúc bấm (trình duyệt chặn `window.open` gọi sau khi chờ mạng), tải xong mới gán địa chỉ.
 */
export async function openContractPdf(id: string, kind: 'pdf' | 'preview-pdf', filename: string) {
  const win = kind === 'preview-pdf' ? window.open('', '_blank') : null
  try {
    const res = await platformApi.get(`/platform/contracts/${id}/${kind}`, { responseType: 'blob' })
    const url = URL.createObjectURL(res.data as Blob)
    if (kind === 'preview-pdf') {
      if (win) win.location.href = url
      else window.open(url, '_blank')
    } else {
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
  } catch (err) {
    win?.close()
    throw err
  }
}

// ── API công khai (không đăng nhập) — trang /ky/:token ────────────────

export class PublicApiError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message) }
}

async function publicCall<T>(method: 'GET' | 'POST', token: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}/public/contracts/${encodeURIComponent(token)}${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    referrerPolicy: 'no-referrer',
  })
  const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
  if (!res.ok) throw new PublicApiError(res.status, data.code ?? 'ERROR', data.error ?? 'Có lỗi xảy ra — vui lòng thử lại')
  return data as T
}

export const fetchPublicContract = (token: string) => publicCall<PublicContract>('GET', token, '')
export const requestContractOtp = (token: string) => publicCall<{ sent: boolean; to: string; ttlMinutes: number; cooldownSeconds: number }>('POST', token, '/otp', {})
export const signPublicContract = (token: string, body: { otp: string; signerName: string; signature: string; agree: boolean }) =>
  publicCall<{ signed: boolean; number: string; signedAt: string; emailSent: boolean }>('POST', token, '/sign', body)
export const declinePublicContract = (token: string, reason: string) => publicCall<{ declined: boolean }>('POST', token, '/decline', { reason })
